// REST paths and response normalizers for the generic API (AGENTS.md section 5). Normalizers accept the documented
// shapes plus a few tolerant variants, so a small server-side shape change degrades gracefully instead of crashing.
import type { Query } from './api'
import type { RecordRow } from './schema'

const enc = encodeURIComponent

export const paths = {
  me: '/auth/me',
  login: '/auth/login',
  logout: '/auth/logout',
  schema: '/api/v1/_schema',
  search: '/api/v1/search',
  apiKeys: '/api/v1/api-keys',
  apiKey: (id: string) => `/api/v1/api-keys/${enc(id)}`,
  list: (plural: string) => `/api/v1/${plural}`,
  record: (plural: string, idOrKey: string) => `/api/v1/${plural}/${enc(idOrKey)}`,
  action: (plural: string, id: string, action: 'archive' | 'restore' | 'revert') =>
    `/api/v1/${plural}/${enc(id)}/${action}`,
  history: (plural: string, id: string) => `/api/v1/${plural}/${enc(id)}/history`,
  /** Notes on a record: GET (newest first, authorId expanded) and POST { body }. */
  comments: (plural: string, id: string) => `/api/v1/${plural}/${enc(id)}/comments`,
  /** Every link of a record, grouped by link name. */
  recordLinks: (plural: string, id: string) => `/api/v1/${plural}/${enc(id)}/links`,
  links: (linkName: string) => `/api/v1/links/${enc(linkName)}`,
  files: '/api/v1/files',
  /** Same-origin download of a stored file (sha256 verified server-side, logged to the audit chain). */
  fileContent: (id: string) => `/api/v1/files/${enc(id)}/content`,
}

/**
 * List query for the company singleton (GET /api/v1/companies). Every module reads the oldest row (the server's
 * getCompany rule), so ask for that row explicitly instead of relying on the list's default sort.
 */
export const COMPANY_QUERY = { limit: 1, sort: 'createdAt' } as const

/** File purpose for uploads made from an entity's form. */
export function filePurposeFor(module: string): string {
  const map: Record<string, string> = {
    evidence: 'EVIDENCE',
    controls: 'EVIDENCE',
    policies: 'POLICY',
    trust: 'TRUST_DOCUMENT',
    vendors: 'VENDOR_DOCUMENT',
    audits: 'AUDIT_ATTACHMENT',
    personnel: 'USER_DOCUMENT',
    devices: 'DEVICE_DOCUMENT',
    settings: 'LOGO',
    monitoring: 'SNAPSHOT',
  }
  return map[module] ?? 'OTHER'
}

/** Body for POST /:id/revert. */
export function revertBody(version: number): Record<string, unknown> {
  return { version }
}

export interface ListParams {
  q?: string
  /** Field name, prefix with - for descending. */
  sort?: string
  limit?: number
  cursor?: string | null
  includeArchived?: boolean
  /** Already-encoded API filters, e.g. { status__in: 'READY,NOT_READY', ownerId: '<uuid>' }. */
  filters?: Record<string, string>
  /** Reference fields to expand into { id, display, ... } summaries under row._expanded. */
  expand?: string[]
}

const RESERVED = new Set(['q', 'sort', 'limit', 'cursor', 'include_archived', 'expand'])

export function listQuery(p: ListParams): Query {
  const q: Query = {}
  for (const [k, v] of Object.entries(p.filters ?? {})) {
    if (!RESERVED.has(k) && v !== '') q[k] = v
  }
  if (p.q?.trim()) q.q = p.q.trim()
  if (p.sort) q.sort = p.sort
  if (p.limit) q.limit = p.limit
  if (p.cursor) q.cursor = p.cursor
  if (p.includeArchived) q.include_archived = 'true'
  if (p.expand?.length) q.expand = [...p.expand].sort().join(',')
  return q
}

/** Summary of a referenced record (expand=, links, search). */
export interface RecordSummary {
  id: string
  entity: string
  plural: string
  display: string | null
  archived?: boolean
  /** The caller cannot read that entity; only the id is known. */
  restricted?: boolean
  /** The referenced row no longer exists. */
  missing?: boolean
  [field: string]: unknown
}

/** The expanded summary of a reference field on a row, if the API returned one. */
export function expandedOf(
  row: Record<string, unknown> | null | undefined,
  field: string,
): RecordSummary | RecordSummary[] | null | undefined {
  const exp = row?._expanded as Record<string, unknown> | undefined
  if (!exp || typeof exp !== 'object' || !(field in exp)) return undefined
  return exp[field] as RecordSummary | RecordSummary[] | null
}

export interface ListResult<T = RecordRow> {
  data: T[]
  nextCursor: string | null
  total: number
}

export function normalizeList<T = RecordRow>(raw: unknown): ListResult<T> {
  if (Array.isArray(raw)) return { data: raw as T[], nextCursor: null, total: raw.length }
  const r = (raw ?? {}) as { data?: unknown; nextCursor?: unknown; total?: unknown }
  const data = Array.isArray(r.data) ? (r.data as T[]) : []
  return {
    data,
    nextCursor: typeof r.nextCursor === 'string' && r.nextCursor ? r.nextCursor : null,
    total: typeof r.total === 'number' ? r.total : data.length,
  }
}

/** A single record: either the row itself or { data: row }. */
export function normalizeRecord(raw: unknown): RecordRow {
  const r = raw as { data?: unknown; id?: unknown } | null
  if (r && typeof r === 'object' && !('id' in r) && r.data && typeof r.data === 'object')
    return r.data as RecordRow
  return r as RecordRow
}

export type HistoryAction = 'CREATE' | 'UPDATE' | 'ARCHIVE' | 'RESTORE' | 'REVERT' | 'IMPORT' | string
export type ActorType = 'HUMAN' | 'AUDITOR' | 'AGENT' | 'SYSTEM' | string

export interface HistoryEntry {
  version: number
  action: HistoryAction
  at: string
  actorType: ActorType
  actorName: string
  actorPrincipalId: string | null
  onBehalfOfUserId: string | null
  reason: string | null
  requestId: string | null
  diff: Record<string, { from: unknown; to: unknown }>
}

export function normalizeHistory(raw: unknown): HistoryEntry[] {
  const arr = Array.isArray(raw) ? raw : Array.isArray((raw as any)?.data) ? (raw as any).data : []
  return (arr as any[])
    .filter((e) => e && typeof e.version === 'number')
    .map((e) => ({
      version: e.version,
      action: String(e.action ?? 'UPDATE'),
      at: String(e.at ?? e.createdAt ?? ''),
      actorType: String(e.actorType ?? 'SYSTEM'),
      actorName: String(e.actorName ?? 'unknown'),
      actorPrincipalId: e.actorPrincipalId ?? null,
      onBehalfOfUserId: e.onBehalfOfUserId ?? null,
      reason: typeof e.reason === 'string' && e.reason ? e.reason : null,
      requestId: e.requestId ?? null,
      diff: e.diff && typeof e.diff === 'object' && !Array.isArray(e.diff) ? e.diff : {},
    }))
    .sort((a, b) => b.version - a.version)
}

export interface LinkGroup {
  /** Key in the response: the link name, or <name>__reverse for self-links seen from the other side. */
  key: string
  link: string
  description: string
  direction: 'from' | 'to'
  /** The entity on the other side. */
  entity: string
  plural: string
  /** The caller cannot read the other side. */
  restricted: boolean
  total: number
  items: RecordSummary[]
}

/** GET /api/v1/:plural/:id/links -> { data: { [linkName]: group } }. */
export function normalizeLinkGroups(raw: unknown): LinkGroup[] {
  const r = (raw ?? {}) as { data?: unknown }
  const data = (r.data && typeof r.data === 'object' && !Array.isArray(r.data) ? r.data : {}) as Record<
    string,
    any
  >
  return Object.entries(data)
    .filter(([, g]) => g && typeof g === 'object' && typeof g.entity === 'string')
    .map(([key, g]) => ({
      key,
      link: String(g.link ?? key),
      description: String(g.description ?? ''),
      direction: g.direction === 'to' ? ('to' as const) : ('from' as const),
      entity: g.entity,
      plural: String(g.plural ?? ''),
      restricted: !!g.restricted,
      total: typeof g.total === 'number' ? g.total : Array.isArray(g.items) ? g.items.length : 0,
      items: Array.isArray(g.items) ? g.items.filter((i: any) => i && typeof i.id === 'string') : [],
    }))
}

export interface SearchHit {
  entity: string
  plural: string | null
  id: string
  display: string
  /** Best-effort key (code/key property). Prefer keyOfHit() with the schema, which knows each entity's key field. */
  key: string | null
  /** The summary as served (its minimal fields include the entity's key field under its own name). */
  raw: Record<string, unknown>
}

/** The record's human key (e.g. DCF-37) for a search hit, using the entity's declared key field when known. */
export function keyOfHit(hit: SearchHit, keyField: string | null | undefined): string | null {
  if (keyField) {
    const v = hit.raw[keyField]
    if (typeof v === 'string' && v) return v
  }
  return hit.key
}

export interface SearchGroup {
  entity: string
  plural: string | null
  label: string
  total: number
  items: SearchHit[]
}

function toHit(h: any, entityHint?: string, pluralHint?: string | null): SearchHit | null {
  if (!h || typeof h !== 'object') return null
  const entity = h.entity ?? h.entityType ?? entityHint
  const id = h.id ?? h.entityId
  if (typeof entity !== 'string' || typeof id !== 'string') return null
  const display = h.display ?? h.title ?? h.name ?? h.label ?? id
  const key = typeof h.code === 'string' ? h.code : typeof h.key === 'string' ? h.key : null
  return {
    entity,
    plural: h.plural ?? pluralHint ?? null,
    id,
    display: String(display),
    key,
    raw: h as Record<string, unknown>,
  }
}

/**
 * GET /api/v1/search -> { q, data: [{ entity, plural, labelPlural, total, items: [summary] }], total }.
 * Also accepts a flat list of hits.
 */
export function normalizeSearch(raw: unknown): SearchGroup[] {
  const r = (raw ?? {}) as Record<string, unknown>
  const arr = Array.isArray(raw)
    ? raw
    : Array.isArray(r.data)
      ? (r.data as unknown[])
      : Array.isArray(r.results)
        ? (r.results as unknown[])
        : []
  const grouped = arr.every((g: any) => g && typeof g === 'object' && Array.isArray(g.items))
  if (grouped && arr.length) {
    return (arr as any[])
      .map((g) => ({
        entity: String(g.entity),
        plural: typeof g.plural === 'string' ? g.plural : null,
        label: String(g.labelPlural ?? g.label ?? g.entity),
        total: typeof g.total === 'number' ? g.total : g.items.length,
        items: (g.items as unknown[]).map((h) => toHit(h, g.entity, g.plural)).filter(Boolean) as SearchHit[],
      }))
      .filter((g) => g.items.length)
  }
  const byEntity = new Map<string, SearchGroup>()
  for (const h of arr) {
    const hit = toHit(h)
    if (!hit) continue
    const g = byEntity.get(hit.entity) ?? {
      entity: hit.entity,
      plural: hit.plural,
      label: hit.entity,
      total: 0,
      items: [],
    }
    g.items.push(hit)
    g.total += 1
    byEntity.set(hit.entity, g)
  }
  return [...byEntity.values()]
}
